import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sb_token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = not authed
  const [checking, setChecking] = useState(true);
  const [token, setTokenState] = useState(() =>
    localStorage.getItem(TOKEN_KEY),
  );

  const setToken = useCallback((t) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
    setTokenState(t);
  }, []);

  const fetchMe = useCallback(async (t) => {
    if (!t) {
      setUser(null);
      setChecking(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUser(data);
    } catch (_e) {
      setUser(null);
      localStorage.removeItem(TOKEN_KEY);
      setTokenState(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    fetchMe(token);
  }, [token, fetchMe]);

  const login = useCallback(
    async ({ identifier, password, rememberMe }) => {
      const { data } = await axios.post(`${API}/auth/login`, {
        identifier,
        password,
        remember_me: !!rememberMe,
      });
      setToken(data.token);
      setUser(data.user);
      return data.user;
    },
    [setToken],
  );

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, [setToken]);

  const value = useMemo(
    () => ({ user, token, checking, login, logout }),
    [user, token, checking, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
